import { useState } from 'react';
import type { ApiErrorKind } from '../api/errors';
import type { Problem } from '../api/types';
import { PROBLEM_HINTS, problemLabel } from '../lib/labels';
import { formatNumber } from '../lib/format';
import { wireTitle } from '../lib/rtl';
import { PROBLEM_TONE } from '../lib/status';
import { Chip } from './Chip';
import { Icon } from './Icon';
import { Mono } from './Mono';

export interface ProblemPanelProps {
  /** The RFC 9457 body, when the server sent one. */
  problem?: Problem | null;
  /** Failure kind when there is no problem document. */
  kind?: ApiErrorKind;
  /** Transport/contract message from the provider. */
  message?: string;
  /** `Retry-After` (seconds) — only 429 carries it, as a header. */
  retryAfterSeconds?: number;
  /** What the operator was trying to do, e.g. «فرمان reboot». */
  heading?: string;
  onRetry?: () => void;
  onDismiss?: () => void;
  /** Raw body excerpt when the response did not conform to the contract. */
  rawBody?: string;
  className?: string;
}

/**
 * The contract's error surface, built once and reused everywhere: it shows the
 * stable `code` (the thing automation keys on), the HTTP status, the server
 * `detail`, the Persian operator guidance for that code, and the raw
 * `application/problem+json` for tickets.
 */
export function ProblemPanel({
  problem,
  kind = 'problem',
  message,
  retryAfterSeconds,
  heading,
  onRetry,
  onDismiss,
  rawBody,
  className = '',
}: ProblemPanelProps) {
  const [showRaw, setShowRaw] = useState(false);

  const tone = problem
    ? PROBLEM_TONE[problem.code]
    : kind === 'pending-endpoint'
      ? 'neutral'
      : 'critical';
  const code = problem?.code;
  const title = problem ? problemLabel(problem.code) : (heading ?? 'خطای ارتباط');
  const raw = rawBody ?? (problem ? JSON.stringify(problem, null, 2) : (message ?? ''));

  return (
    <div
      className={`panel border-surface-strong bg-surface-raised animate-enter ${className}`}
      role="alert"
      aria-live="polite"
      data-testid="problem-panel"
      data-code={code ?? kind}
    >
      <div className="flex items-start gap-3 p-panel">
        <Icon
          name={kind === 'pending-endpoint' ? 'info' : 'warning'}
          className={`mt-0.5 ${kind === 'pending-endpoint' ? 'text-ink-faint' : 'text-danger'}`}
          size={18}
        />
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <Chip tone={tone} label={title} dot={false} />
            {code ? (
              <span className="mono rounded-chip border border-surface-line bg-surface-void px-1.5 py-[1px] text-caption text-ink-dim">
                {code}
              </span>
            ) : null}
            {problem ? (
              <span className="text-caption text-ink-faint" data-testid="problem-status">
                HTTP {formatNumber(problem.status)}
              </span>
            ) : null}
            {retryAfterSeconds !== undefined ? (
              <span className="text-caption text-severity-warning-ink" data-testid="retry-after">
                تلاش مجدد پس از {formatNumber(retryAfterSeconds)} ثانیه
              </span>
            ) : null}
          </div>

          {problem?.detail ? (
            <p dir="ltr" className="mt-2 text-body text-ink text-left">
              {problem.detail}
            </p>
          ) : null}

          {!problem && message ? <p className="mt-2 text-body text-ink-dim">{message}</p> : null}

          {code && PROBLEM_HINTS[code] ? (
            <p className="mt-2 text-body text-ink-dim" data-testid="problem-hint">
              {PROBLEM_HINTS[code]}
            </p>
          ) : null}

          {problem ? (
            <dl className="mt-2 grid gap-1 text-caption text-ink-faint">
              <div className="flex gap-2">
                <dt>type:</dt>
                <dd>
                  <Mono value={problem.type} />
                </dd>
              </div>
              {problem.instance ? (
                <div className="flex gap-2">
                  <dt>instance:</dt>
                  <dd>
                    <Mono value={problem.instance} title={wireTitle(problem.instance)} />
                  </dd>
                </div>
              ) : null}
              <div className="flex gap-2">
                <dt>title:</dt>
                <dd>
                  <Mono value={problem.title} />
                </dd>
              </div>
            </dl>
          ) : null}

          <div className="mt-3 flex flex-wrap items-center gap-2">
            <button
              type="button"
              className="btn btn-ghost btn-sm"
              onClick={() => setShowRaw((v) => !v)}
              aria-expanded={showRaw}
            >
              {showRaw ? 'بستن بدنهٔ خام' : 'مشاهدهٔ بدنهٔ خام'}
            </button>
            {onRetry ? (
              <button type="button" className="btn btn-primary btn-sm" onClick={onRetry}>
                تلاش مجدد
              </button>
            ) : null}
            {onDismiss ? (
              <button type="button" className="btn btn-ghost btn-sm" onClick={onDismiss}>
                بستن
              </button>
            ) : null}
          </div>

          {showRaw ? (
            <pre className="code-block mt-3" data-testid="problem-raw">
              {raw}
            </pre>
          ) : null}
        </div>
      </div>
    </div>
  );
}
